import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import DocumentService from '../../Services/documents.service';

/**
 * Compute credit/debit sums from PaymentAcceptance documents (checks, creditCards, cash, bankTransactions).
 * Same logic as backend CalculateCreditDebitSum for PaymentAcceptance; no API call.
 */
export function computeCreditDebitFromPaymentAcceptanceDocuments(documents) {
    if (!Array.isArray(documents) || documents.length === 0) {
        return { creditSum: 0, debitSum: 0 };
    }
    let creditSum = 0;
    let debitSum = 0;
    for (const doc of documents) {
        const checks = doc.checks || [];
        const creditCards = doc.creditCards || [];
        const cash = doc.cash || [];
        const bankTransactions = doc.bankTransactions || [];
        const offsets = doc.offsets || [];
        for (const row of checks) {
            if (row.creditAccount) creditSum += Number(row.amount) || 0;
            if (row.debitAccount) debitSum += Number(row.amount) || 0;
        }
        for (const row of creditCards) {
            if (row.creditAccount) creditSum += Number(row.amount) || 0;
            if (row.debitAccount) debitSum += Number(row.amount) || 0;
        }
        for (const row of cash) {
            if (row.creditAccount) creditSum += Number(row.amount) || 0;
            if (row.debitAccount) debitSum += Number(row.amount) || 0;
        }
        for (const row of bankTransactions) {
            if (row.creditAccount) creditSum += Number(row.amount) || 0;
            if (row.debitAccount) debitSum += Number(row.amount) || 0;
        }
        for (const row of offsets) {
            if (row.creditAccount) creditSum += Number(row.amount) || 0;
            if (row.debitAccount) debitSum += Number(row.amount) || 0;
        }
    }
    return {
        creditSum: Math.round(creditSum * 100) / 100,
        debitSum: Math.round(debitSum * 100) / 100,
    };
}

/**
 * Fetches PaymentAcceptance page data via single API: Documents/GetPaymentAcceptancePageData.
 * Returns documents, credit/debit sum, balance control (when range), and isClosedReportingMonth.
 */
export const fetchPaymentAcceptancePageData = createAsyncThunk(
    'paymentAcceptancePage/fetchData',
    async (
        {
            caseId,
            type,
            startDate,
            endDate,
            isRange,
            useRegistrationMonth,
            requestKey,
            token,
            backgroundRefresh,
        },
        { rejectWithValue }
    ) => {
        try {
            const start = startDate instanceof Date ? startDate : new Date(startDate);
            const end = endDate instanceof Date ? endDate : new Date(endDate);
            const dto = {
                caseId,
                startDate: start.toISOString(),
                endDate: end.toISOString(),
                useRegistrationMonth: !!useRegistrationMonth,
                isRange: !!isRange,
            };
            const response = await DocumentService.GetPaymentAcceptancePageData(dto, token);
            if (!response.ok) {
                const errText = await response.text();
                return rejectWithValue(errText || 'Failed to fetch payment acceptance page data');
            }
            const data = await response.json();
            const documents = Array.isArray(data.documents) ? data.documents : (data.Documents ?? data.documents?.result ?? []);
            const creditSum = data.creditSum ?? data.CreditSum ?? 0;
            const debitSum = data.debitSum ?? data.DebitSum ?? 0;
            const missingCards = data.missingCards ?? data.MissingCards ?? [];
            const creditDebitSum = {
                creditSum,
                debitSum,
                missingCards,
            };
            const balCredit = data.balanceControlCreditAmount ?? data.BalanceControlCreditAmount;
            const balDebit = data.balanceControlDebitAmount ?? data.BalanceControlDebitAmount;
            const balanceControl = (balCredit != null && balDebit != null)
                ? { creditAmount: balCredit, debitAmount: balDebit }
                : null;
            return {
                documents,
                creditDebitSum,
                balanceControl,
                isClosedReportingMonth: data.isClosedReportingMonth ?? data.IsClosedReportingMonth ?? false,
                isRange: !!isRange,
                requestKey,
                backgroundRefresh,
            };
        } catch (error) {
            return rejectWithValue(error?.message || 'Unknown error');
        }
    }
);

const initialState = {
    documents: [],
    creditDebitSum: null,
    balanceControl: null,
    recalculatedCreditDebit: null,
    isClosedReportingMonth: false,
    loading: false,
    error: null,
    expectedFetchKey: null,
    lastFetchedRequestKey: null,
};

const paymentAcceptancePageSlice = createSlice({
    name: 'paymentAcceptancePage',
    initialState,
    reducers: {
        resetPaymentAcceptancePage: () => initialState,
        setDocuments: (state, action) => {
            state.documents = action.payload ?? [];
        },
        setMonthClosed: (state, action) => {
            state.isClosedReportingMonth = action.payload;
        },
        /** Set credit/debit for filtered subset (computed client-side from documents; no API call). */
        setRecalculatedCreditDebit: (state, action) => {
            const { creditAmount, debitAmount } = action.payload ?? {};
            state.recalculatedCreditDebit =
                creditAmount != null && debitAmount != null
                    ? { creditAmount: Number(creditAmount), debitAmount: Number(debitAmount) }
                    : null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPaymentAcceptancePageData.pending, (state, action) => {
                const backgroundRefresh = action.meta?.arg?.backgroundRefresh;
                const hasExistingData = (state.documents?.length ?? 0) > 0;
                if (!(backgroundRefresh && hasExistingData)) state.loading = true;
                state.error = null;
                const key = action.meta?.arg?.requestKey;
                if (key != null) state.expectedFetchKey = key;
            })
            .addCase(fetchPaymentAcceptancePageData.fulfilled, (state, action) => {
                const r = action.payload;
                const requestKey = r.requestKey;
                if (requestKey != null && state.expectedFetchKey != null && requestKey !== state.expectedFetchKey) {
                    state.loading = false;
                    return;
                }
                state.documents = Array.isArray(r.documents) ? r.documents : [];
                state.creditDebitSum = r.creditDebitSum ?? null;
                state.balanceControl = r.balanceControl ?? null;
                state.recalculatedCreditDebit = null;
                state.isClosedReportingMonth = r.isClosedReportingMonth ?? false;
                state.loading = false;
                state.error = null;
                if (requestKey != null) state.lastFetchedRequestKey = requestKey;
            })
            .addCase(fetchPaymentAcceptancePageData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const { resetPaymentAcceptancePage, setDocuments, setMonthClosed, setRecalculatedCreditDebit } = paymentAcceptancePageSlice.actions;
export default paymentAcceptancePageSlice.reducer;
